import {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { BookingInfo } from 'src/api/bookingsView';
import { Building, Floor, Town } from 'src/api/types';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { PlaceType } from 'src/types';

export interface SearchedBookingObject {
  id: number;
  type: PlaceType;
}

export interface SearchBookingObjectContextValue {
  searchedBookingObject: SearchedBookingObject | undefined;
  searchedBookingObjectRef: RefObject<SearchedBookingObject | undefined>;
  setSearchedBookingObject: (value: SearchedBookingObject | undefined) => void;
}

export const SearchBookingObjectContext = createContext<
  SearchBookingObjectContextValue | undefined
>(undefined);
SearchBookingObjectContext.displayName = 'SearchBookingObjectContext';

interface SearchBookingObjectProviderProps {
  children?: ReactNode;
  setTown: (value: Town | undefined) => void;
  setBuilding: (value: Building | undefined) => void;
  setFloor: (value: Floor | undefined) => void;
  setTimeRange?: (from: Date | undefined, to: Date | undefined) => void;
}

export const SearchBookingObjectProvider: FC<
  SearchBookingObjectProviderProps
> = ({ children, setTown, setBuilding, setFloor, setTimeRange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searched = searchParams.get('searched') as string | null;

  const [searchedBookingObject, changeSearchedBookingObject] = useState<
    SearchedBookingObject | undefined
  >();

  const searchedBookingObjectRef = useRef<SearchedBookingObject | undefined>();

  const setSearchedBookingObject = useCallback(
    (value: SearchedBookingObject | undefined) => {
      changeSearchedBookingObject(value);
      searchedBookingObjectRef.current = value;
    },
    [],
  );

  const contextValue = useMemo(
    (): SearchBookingObjectContextValue => ({
      searchedBookingObject,
      setSearchedBookingObject,
      searchedBookingObjectRef,
    }),
    [searchedBookingObject, setSearchedBookingObject],
  );

  useEffect(() => {
    if (!searched) return;

    const searchedBooking = JSON.parse(searched) as BookingInfo;

    setTown(searchedBooking.town);
    setBuilding(searchedBooking.building);
    setFloor(searchedBooking.floor);
    if (searchedBooking.bookedFrom && searchedBooking.bookedTo)
      setTimeRange?.(
        new Date(searchedBooking.bookedFrom),
        new Date(searchedBooking.bookedTo),
      );

    setSearchedBookingObject({
      id: searchedBooking.placeId,
      type: searchedBooking.placeType,
    });

    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('searched');
      return next;
    });
  }, [
    searched,
    setBuilding,
    setFloor,
    setSearchParams,
    setSearchedBookingObject,
    setTimeRange,
    setTown,
  ]);

  return (
    <SearchBookingObjectContext.Provider value={contextValue}>
      {children}
    </SearchBookingObjectContext.Provider>
  );
};

export const useSearchBookingObject = (): SearchBookingObjectContextValue =>
  useRequiredContext(SearchBookingObjectContext);
